/**
 * One tile per app — brief part 1 §6 band 4 (DEC-87).
 *
 * Each tile: the app's name (11px uppercase), ONE figure in the numeric face at 24px, ONE sentence,
 * and a status line at the foot behind a rule. The whole tile is the way into the app's main page,
 * on the run its figure is about. The app's STATE lives here — "3 to decide", "transfers held by
 * gate D2", "nothing proposed" — and not as a notice at the head of the page (§6).
 *
 * **Three tiles, not the designer's four.** The designer drew four columns — Replenishment,
 * Compacting, Pricing, Management — with Management's figure "1 feed late". That figure, and every
 * other Management figure this page could state (open alerts, write-back commands to follow), is
 * already on this screen: in «Copertura dati» and in «Da decidere», side by side above the tiles.
 * A fourth tile would repeat one of them, which is what the brief removes elsewhere («via i
 * duplicati»). The grid is three columns until Management has a figure of its own
 * (team-leader question to the designer, 2026-09-30).
 *
 * Every figure comes from a read the page already makes (`useCockpitReads`). None is a zero it did
 * not read: loading is «—», an empty answer is «—» with the tile's own sentence, a read not
 * published is «—» with «In arrivo», a failure is «—» and the page strip's error (ADR 0014).
 */

import { useTranslation } from 'react-i18next';

import { formatCompactNumber, formatCurrency, formatNumber } from '@/lib/format';
import { ArrivingTag, type ReadState } from '@/planning';

import {
  percentOf,
  type D2Market, type DecisionCounts, type MoveRunFigures, type PriceFigures, type ReplenishmentFigures,
} from './cockpit';

export type AppTileId = 'pricing' | 'replenishment' | 'compacting';

/** Each app's name — the rail's own group labels (`RAIL_APPS`), spelled out for the catalogue check. */
const APP_LABEL: Readonly<Record<AppTileId, string>> = {
  pricing: 'nav.app.pricing',
  replenishment: 'nav.app.replenishment',
  compacting: 'nav.app.compacting',
};

/** A tile's status line: a glyph and words, never the colour alone (design/09 §12.6). */
type Tone = 'act' | 'done' | 'held' | 'quiet' | 'failed';

const TONE: Readonly<Record<Tone, { readonly glyph: string; readonly ink: string }>> = {
  act: { glyph: '●', ink: 'text-ink-primary' },
  done: { glyph: '✓', ink: 'text-status-good-strong' },
  held: { glyph: '◐', ink: 'text-ink-label' },
  quiet: { glyph: '—', ink: 'text-ink-label' },
  failed: { glyph: '!', ink: 'text-status-critical-strong' },
};

export interface TileStatus {
  readonly tone: Tone;
  readonly key: string;
  readonly values?: Readonly<Record<string, string | number>>;
}

export interface TileContent {
  /** The figure as shown, or `null` — «—». */
  readonly figure: string | null;
  /** The exact figure, when `figure` is compact. */
  readonly exact?: string;
  /** Data «B»: the read is not published by this platform (brief §4) — «—» and «In arrivo». */
  readonly arriving: boolean;
  readonly line: { readonly key: string; readonly values?: Readonly<Record<string, string | number>> };
  readonly status: TileStatus;
}

/** What a read's state says when it has no figure — `null` when it has one. */
function withoutFigure(state: ReadState<unknown>, emptyKey: string): Omit<TileContent, 'figure'> | null {
  switch (state.kind) {
    case 'populated': return null;
    case 'loading': return { arriving: false, line: { key: 'state.loading' }, status: { tone: 'quiet', key: 'overview.apps.status.reading' } };
    case 'awaiting': return { arriving: false, line: { key: emptyKey }, status: { tone: 'quiet', key: 'overview.apps.status.nothing' } };
    case 'unavailable':
      if (state.reason === 'not-built') {
        return { arriving: true, line: { key: 'overview.apps.notPublished' }, status: { tone: 'quiet', key: 'overview.apps.status.notPublished' } };
      }
      return { arriving: false, line: { key: 'overview.tiles.didNotLoad' }, status: { tone: 'failed', key: 'overview.apps.status.failed' } };
  }
}

/** "N to decide", "nothing left", or "not counted" — from the decision summary, never guessed. */
function pendingStatus(pending: number | null): TileStatus {
  if (pending === null) return { tone: 'held', key: 'overview.apps.status.notCounted' };
  if (pending === 0) return { tone: 'done', key: 'overview.apps.status.allDecided' };
  return { tone: 'act', key: 'overview.apps.status.pending', values: { count: pending } };
}

const NO_RUN: TileContent = {
  figure: null, arriving: false, line: { key: 'overview.apps.noRun' }, status: { tone: 'quiet', key: 'overview.apps.status.noRun' },
};

export interface TileInputs {
  readonly runKnown: boolean;
  readonly prices: { readonly state: ReadState<unknown>; readonly figures: PriceFigures | null };
  /** The PRICE run's summary (F16) — its pending price steps. */
  readonly priceDecisions: DecisionCounts | null;
  readonly replenishment: { readonly state: ReadState<unknown>; readonly figures: ReplenishmentFigures | null };
  /**
   * The one move run (bata-reqs ADR 9002 stage 3): the live move-run directory's state, and the run the
   * decision summary counts — else the market's newest — as figures; `null` when the directory lists none.
   */
  readonly moves: { readonly state: ReadState<unknown>; readonly figures: MoveRunFigures | null };
  /** The cycle run's summary — pending proposals, and the pending moves of the move run it names. */
  readonly decisions: DecisionCounts | null;
  /** Gate D2 in the run's own market, when the directory publishes it. */
  readonly d2: D2Market | undefined;
}

/**
 * Each tile's figure, sentence and status. Pure, so what a tile claims is asserted on rows.
 *
 *   Pricing        the run's expected margin recovery (one currency, else «—»); pending steps
 *   Replenishment  the units the run proposed, against the doors' need; pending proposals
 *   Compacting     the lines the market's one move run proposed, and those it held back; gate D2,
 *                  else its pending moves — never the cycle run's transfer book, which the one run
 *                  retired (platform v0.126.0, owner ruling 2026-11-02)
 */
export function tileContents(input: TileInputs, locale: string): Readonly<Record<AppTileId, TileContent>> {
  const compact = (value: number): string => formatCompactNumber(value, locale).text;
  const exact = (value: number): string => formatCompactNumber(value, locale).exact;

  const pricing = ((): TileContent => {
    if (!input.runKnown) return NO_RUN;
    const figures = input.prices.figures;
    if (figures === null) return { figure: null, ...(withoutFigure(input.prices.state, 'overview.apps.pricing.none') ?? NO_RUN) };
    const valued = input.priceDecisions?.valued ?? null;
    const status = pendingStatus(valued === null ? null : valued.priceStepsPending);
    const line = figures.weeks === null
      ? { key: 'overview.apps.pricing.lineNoWeeks', values: { steps: figures.steps } }
      : { key: 'overview.apps.pricing.line', values: { steps: figures.steps, weeks: figures.weeks } };
    if (figures.recovery === null) {
      return { figure: null, arriving: false, line: { key: 'overview.apps.pricing.noSingleCurrency' }, status };
    }
    return {
      figure: `${compact(figures.recovery.amount)} ${figures.recovery.currency}`,
      exact: formatCurrency(figures.recovery.amount, figures.recovery.currency, locale),
      arriving: false,
      line,
      status,
    };
  })();

  const replenishment = ((): TileContent => {
    if (!input.runKnown) return NO_RUN;
    const figures = input.replenishment.figures;
    if (figures === null) {
      return { figure: null, ...(withoutFigure(input.replenishment.state, 'overview.tiles.replenishment.emptyTitle') ?? NO_RUN) };
    }
    const fill = percentOf(figures.units, figures.need);
    return {
      figure: compact(figures.units),
      exact: exact(figures.units),
      arriving: false,
      line: fill === null
        ? { key: 'overview.apps.replenishment.lineNoNeed', values: { stores: figures.stores, dropped: figures.droppedStores } }
        : {
          key: 'overview.apps.replenishment.line',
          values: { stores: figures.stores, dropped: figures.droppedStores, fill: formatNumber(fill, locale, { fractionDigits: 1, fixed: true }) },
        },
      status: pendingStatus(input.decisions?.proposalsPending ?? null),
    };
  })();

  const compacting = ((): TileContent => {
    if (!input.runKnown) return NO_RUN;
    const run = input.moves.figures;
    if (run === null) {
      // The directory answered and lists no run of this market: the one run has not run here yet.
      if (input.moves.state.kind === 'populated' || input.moves.state.kind === 'awaiting') {
        return { figure: null, arriving: false, line: { key: 'overview.apps.compacting.noRun' }, status: { tone: 'quiet', key: 'overview.apps.status.nothing' } };
      }
      return { figure: null, ...(withoutFigure(input.moves.state, 'overview.apps.compacting.noRun') ?? NO_RUN) };
    }
    // A refused run decided nothing: its total is still a number (0), and the status says why there is none.
    if (run.refused) {
      return {
        figure: compact(run.proposed),
        exact: exact(run.proposed),
        arriving: false,
        line: { key: 'overview.apps.compacting.lineRefused' },
        status: { tone: 'failed', key: 'overview.apps.compacting.statusRefused' },
      };
    }
    const d2 = input.d2;
    // The pending moves are the summary's, of the run it names: stated only when that run is the one shown.
    const decisions = input.decisions;
    const pending = decisions !== null && decisions.transfersPending !== null && decisions.transfersMoveRunId === run.runId
      ? decisions.transfersPending
      : null;
    const status: TileStatus = d2 !== undefined && !d2.enabled
      ? { tone: 'held', key: 'overview.tiles.d2.held' }
      : pending !== null
        ? pendingStatus(pending)
        : d2 !== undefined
          ? { tone: 'done', key: 'overview.tiles.d2.enabled' }
          : { tone: 'held', key: 'overview.apps.status.notCounted' };
    return {
      figure: compact(run.proposed),
      exact: exact(run.proposed),
      arriving: false,
      line: { key: 'overview.apps.compacting.line', values: { count: run.proposed, held: run.held } },
      status,
    };
  })();

  return { pricing, replenishment, compacting };
}

function Tile({ app, content, onOpen }: {
  readonly app: AppTileId;
  readonly content: TileContent;
  readonly onOpen: (() => void) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const tone = TONE[content.status.tone];
  const body = (
    <>
      <span className="text-ink-label block text-[11px] uppercase tracking-[0.1em]">{t(APP_LABEL[app])}</span>
      <span
        className={`font-numeric block text-[24px] font-semibold leading-8 tabular-nums ${content.figure === null ? 'text-ink-secondary' : 'text-ink-primary'}`}
        title={content.exact}
      >
        {content.figure ?? t('overview.tiles.notMeasured')}
      </span>
      {content.arriving && <span className="block"><ArrivingTag /></span>}
      <span className="text-ink-primary block text-[13px] leading-5">{t(content.line.key, content.line.values ?? {})}</span>
      <span className={`border-hairline ${tone.ink} mt-auto flex items-center gap-[6px] border-t pt-2 text-[12px]`} data-tile-status={content.status.tone}>
        <span aria-hidden className="font-numeric">{tone.glyph}</span>
        <span>{t(content.status.key, content.status.values ?? {})}</span>
      </span>
    </>
  );
  const box = 'flex h-full w-full flex-col gap-2 px-6 py-[18px] text-start';
  return (
    <section aria-label={t(APP_LABEL[app])} className="border-hairline min-w-0 border-e last:border-e-0" data-app-tile={app} data-layout-block="">
      {onOpen === undefined ? <div className={box}>{body}</div> : (
        <button
          type="button"
          onClick={onOpen}
          className={`${box} hover:bg-surface-emphasis focus-visible:outline-focus-ring focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2`}
        >
          {body}
        </button>
      )}
    </section>
  );
}

export interface AppTilesProps extends TileInputs {
  readonly onOpen: (app: AppTileId) => void;
}

export function AppTiles({ onOpen, ...input }: AppTilesProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const tiles = tileContents(input, i18n.language);
  const open = (app: AppTileId): (() => void) | undefined => (input.runKnown ? () => onOpen(app) : undefined);

  return (
    // A band of the page grid (brief part 3 §B5, ADR 9038): one equal column per app, one height.
    <section aria-label={t('overview.apps.title')} className="border-hairline bg-surface-raised grid items-stretch border-t sm:grid-cols-3" data-app-tiles="" data-layout-row="cockpit-apps">
      <Tile app="pricing" content={tiles.pricing} onOpen={open('pricing')} />
      <Tile app="replenishment" content={tiles.replenishment} onOpen={open('replenishment')} />
      <Tile app="compacting" content={tiles.compacting} onOpen={open('compacting')} />
    </section>
  );
}
